import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useGetAttemptsQuery, useGetQuizzesQuestionsQuery } from '../../services/SkillsManagement';

const ReviewAttemptsPDFExport = ({ open, onClose, quizId, userId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [isGenerating, setIsGenerating] = React.useState(false);
  const [exportOptions, setExportOptions] = React.useState({
    orientation: 'portrait',
    title: 'Quiz Attempts Report',
    subtitle: '',
  });

  const handleOptionChange = (field, value) => {
    setExportOptions((prev) => ({ ...prev, [field]: value }));
  };

  const { data: attemptsData, isFetching } = useGetAttemptsQuery(
    { quizId, userId, page: 1, limit: 200 },
    { refetchOnMountOrArgChange: true, skip: !quizId || !userId || !open }
  );

  const attempts = React.useMemo(() => attemptsData?.data?.quizAttempts || attemptsData?.quizAttempts || [], [attemptsData]);

  // Fetch questions once (same as ReviewAllAttempts)
  const { data: questionsData } = useGetQuizzesQuestionsQuery(
    { quizId, page: 1, limit: 1000 },
    { refetchOnMountOrArgChange: true, skip: !quizId || !open }
  );
  const questionsFromHook = React.useMemo(() => questionsData?.data?.questions || [], [questionsData]);

  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString(); } catch { return String(d); }
  };
  const fmtSeconds = (secs) => {
    if (secs == null) return '-';
    const s = Number(secs) || 0;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m > 0) return `${m} min${rs ? ` ${rs} s` : ''}`;
    return `${rs} s`;
  };
  const statusLabel = (s) => {
    const v = String(s || '').toLowerCase();
    if (v === 'in_progress' || v === 'in progress') return 'in progress';
    return v || '-';
  };

  // helpers to call backend directly (mirrors RTK endpoints used in ReviewAllAttempts)
  const authHeaders = () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  };

  const tryFetchJson = async (urls) => {
    let firstError;
    for (const url of urls) {
      try {
        const res = await fetch(url, { method: 'GET', headers: { ...authHeaders() } });
        if (res.ok) return await res.json();
        firstError = firstError || new Error(`${res.status} ${res.statusText} for ${url}`);
      } catch (e) {
        firstError = firstError || e;
      }
    }
    throw firstError || new Error('All endpoints failed');
  };

  // Removed explicit fetch for questions; we rely on the working hook above

  const fetchResponses = async ({ quizId: qid, userId: uid, attemptId: aid }) => {
    const q = new URLSearchParams({ quizId: String(qid), userId: String(uid), attemptId: String(aid), page: '1', limit: '1000' }).toString();
    const p = new URLSearchParams({ page: '1', limit: '1000' }).toString();
    const urls = [
      `/quizzes/responses?${q}`,
      `/api/quizzes/responses?${q}`,
      `/quizzes/attempts/${encodeURIComponent(aid)}/responses?${p}`,
      `/api/quizzes/attempts/${encodeURIComponent(aid)}/responses?${p}`,
    ];
    const json = await tryFetchJson(urls);
    return json?.data?.quizResponses || json?.quizResponses || [];
  };

  const generatePDF = async () => {
    setIsGenerating(true);
    try {
      const pdf = new jsPDF({ orientation: exportOptions.orientation, unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 15;

      const title = exportOptions.title || 'Quiz Attempts Report';
      const subtitle = exportOptions.subtitle || '';

      const quizTitle = attempts[0]?.quiz?.title || `Quiz #${quizId}`;
      const username = attempts[0]?.user?.username || attempts[0]?.user?.name || `User #${userId}`;

      // Header
      pdf.setFontSize(18);
      pdf.setTextColor(40, 40, 40);
      pdf.text(title, margin, margin + 8);
      let yPos = margin + 16;

      if (subtitle) {
        pdf.setFontSize(12);
        pdf.setTextColor(90, 90, 90);
        pdf.text(subtitle, margin, yPos);
        yPos += 8;
      }

      // Meta block
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      const meta = [
        `Employee: ${username}`,
        `Quiz: ${quizTitle}`,
        `Attempts: ${attempts.length}`,
        `Generated: ${new Date().toLocaleString()}`,
      ];
      meta.forEach((t) => { pdf.text(t, margin, yPos); yPos += 6; });
      yPos += 4;

      // Attempts summary table
      const head = [['#', 'Status', 'Score', 'Time Spent', 'Started at', 'Last update']];
      const body = attempts
        .slice()
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
        .map((a, idx) => [
          String(attempts.length - idx),
          statusLabel(a.status),
          a.score != null ? String(a.score) : '-',
          fmtSeconds(a.timeSpent),
          fmtDate(a.startTime || a.createdAt),
          fmtDate(a.endTime || a.updatedAt),
        ]);

      autoTable(pdf, {
        startY: yPos,
        head,
        body,
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [255, 152, 0], textColor: [255, 255, 255] },
        bodyStyles: { textColor: [60, 60, 60] },
        styles: { fontSize: 9, cellPadding: 2 },
      });

      // Build questions index once using hook data
      const qIndex = new Map();
      questionsFromHook.forEach((q) => qIndex.set(q?.questionId || q?.id, q));

      // For each attempt, append a detailed section with all responses
      const sortedAttempts = attempts
        .slice()
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

      const getAnswerText = (resp) => {
        if (resp?.textResponse) return resp.textResponse;
        if (resp?.option?.optionText) return resp.option.optionText;
        const q = qIndex.get(resp?.questionId);
        const opts = Array.isArray(q?.options) ? q.options : [];
        const opt = opts.find((o) => Number(o?.optionId || o?.id) === Number(resp?.optionId));
        return opt?.optionText || opt?.text || (resp?.optionId ? `Option #${resp.optionId}` : '-');
      };

      const statusTag = (s) => {
        const v = String(s || '').toLowerCase();
        if (v === 'in_progress' || v === 'in progress') return 'in progress';
        return v || '-';
      };

      for (let idx = 0; idx < sortedAttempts.length; idx += 1) {
        const a = sortedAttempts[idx];
        pdf.addPage();
        pdf.setFontSize(14);
        pdf.setTextColor(40, 40, 40);
        pdf.text(`Attempt #${sortedAttempts.length - idx}`, margin, 20);

        // Attempt meta line (score, time, status)
        pdf.setFontSize(11);
        pdf.setTextColor(70, 70, 70);
        pdf.text(`Score: ${a.score ?? '-'}`, margin, 28);
        pdf.text(`Time: ${fmtSeconds(a.timeSpent)}`, margin + 45, 28);
        pdf.text(`Status: ${statusTag(a.status)}`, margin + 100, 28);

        const responses = await fetchResponses({ quizId, userId, attemptId: a.attemptId });
        const rows = responses.map((r, i) => {
          const q = qIndex.get(r?.questionId) || {};
          const qText = r?.question?.questionText || q?.questionText || q?.text || `Q#${r?.questionId}`;
          const ans = getAnswerText(r);
          const isOk = !!r?.isCorrect;
          const resLabel = isOk ? 'correct' : 'incorrect';
          const pts = Number(r?.pointsEarned || 0);
          return [String(i + 1), qText, ans, resLabel, String(pts)];
        });

        autoTable(pdf, {
          startY: 36,
          head: [['#', 'Question', 'Your Answer', 'Result', 'Points']],
          body: rows,
          margin: { left: margin, right: margin },
          headStyles: { fillColor: [255, 152, 0], textColor: [255, 255, 255] },
          bodyStyles: { textColor: [60, 60, 60] },
          styles: { fontSize: 9, cellPadding: 2 },
          columnStyles: { 0: { cellWidth: 10 }, 4: { cellWidth: 18, halign: 'right' } },
        });

        // Total score footer
        const totalPts = responses.reduce((acc, r) => acc + Number(r?.pointsEarned || 0), 0);
        const endY = pdf.lastAutoTable?.finalY || 36;
        pdf.setFontSize(11);
        pdf.setTextColor(50, 50, 50);
        pdf.text(`Total score: ${totalPts}`, pageWidth - margin, endY + 8, { align: 'right' });
      }

      const fileName = `quiz-report-${quizId}-user-${userId}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF', e);
    } finally {
      setIsGenerating(false);
      onClose && onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { backgroundColor: theme.palette.mode === 'dark' ? colors.primary[800] : colors.grey[50] } }}
    >
      <DialogTitle sx={{ color: colors.grey[theme.palette.mode === 'dark' ? 700 : 600] }}>
        Export Quiz Report to PDF
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Report Title"
            value={exportOptions.title}
            onChange={(e) => handleOptionChange('title', e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Subtitle (Optional)"
            value={exportOptions.subtitle}
            onChange={(e) => handleOptionChange('subtitle', e.target.value)}
            fullWidth
            size="small"
          />
          <Divider />
          <Typography variant="subtitle2" sx={{ color: colors.grey[700] }}>Page Orientation</Typography>
          <RadioGroup
            row
            value={exportOptions.orientation}
            onChange={(e) => handleOptionChange('orientation', e.target.value)}
          >
            <FormControlLabel value="portrait" control={<Radio />} label="Portrait" />
            <FormControlLabel value="landscape" control={<Radio />} label="Landscape" />
          </RadioGroup>
          <Divider />
          <Typography variant="body2" sx={{ color: colors.grey[600] }}>
            {isFetching ? 'Loading attempts…' : `${attempts.length} attempt(s) ready for export.`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={isGenerating}>Cancel</Button>
        <Button onClick={generatePDF} variant="contained" disabled={isGenerating || isFetching || !attempts.length}
          sx={{ backgroundColor: colors.redAccent[500], '&:hover': { backgroundColor: colors.redAccent[600] } }}
        >
          {isGenerating ? 'Generating…' : 'Export PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewAttemptsPDFExport;
